/* eslint-disable react/prop-types */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    TextField,
    InputAdornment,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

// ─── Parámetros maestros: dónde se configuran y a qué afectan ───
const PARAMETROS = [
    {
        parametro: 'Mín / Máx de Temperatura, Humedad, Amperaje y RPM',
        ruta: '/maquinas/parametros',
        pantalla: 'Parámetros de Máquinas',
        coleccion: 'ParametrosMaquina',
        afecta: 'Colores y parpadeo del Tablero principal; rango de los indicadores circulares; alarma de amperaje (se dispara cuando amperaje ≥ maxAmp).',
    },
    {
        parametro: 'RPM máximo por máquina',
        ruta: '/maquinas/valores-eficiencia',
        pantalla: 'Valores de Eficiencia',
        coleccion: 'ValoresEficiencia',
        afecta: 'Eficiencia guardada cada 10 min y eficiencia en vivo del Tablero = (RPM ÷ RPM máximo) × 100. Alimenta Tabla de Eficiencia y gráficas por día. Se actualiza en máx. 5 min en el backend.',
    },
    {
        parametro: 'Revoluciones por metro (por referencia y máquina)',
        ruta: '/maquinas/referencias',
        pantalla: 'Referencias',
        coleccion: 'Referencia.maquinas[]',
        afecta: 'Conversión de vueltas a metros: metros = revoluciones ÷ rev/metro. Usado en Producción por Referencia, Acumulado Operarios, Donut metros por turno.',
    },
    {
        parametro: 'Rendimiento práctico (por referencia y máquina)',
        ruta: '/maquinas/referencias',
        pantalla: 'Referencias',
        coleccion: 'Referencia.maquinas[]',
        afecta: 'Se muestra como dato de referencia en Producción por Referencia y Programación IA (estimaciones).',
    },
    {
        parametro: 'Horarios de turnos (inicio / fin) por periodo',
        ruta: '/maquinas/formulario-guardar-turnos',
        pantalla: 'Guardar Turnos',
        coleccion: 'ParametrosTurnos',
        afecta: 'Ventanas horarias de la Tabla segmentos horas, asignación de operarios y cálculo de horas por operario.',
    },
    {
        parametro: 'Asignación máquina → referencia (rango de fechas)',
        ruta: '/maquinas/asignar-referencia',
        pantalla: 'Asignar Referencia',
        coleccion: 'AsignacionReferencia',
        afecta: 'Define qué rev/metro se usa para cada máquina cada día. Sin asignación, la producción en metros sale en 0.',
    },
    {
        parametro: 'Asignación operario → máquina / turno',
        ruta: '/maquinas/asignar-operarios',
        pantalla: 'Asignar Operarios',
        coleccion: 'AsignacionOperario',
        afecta: 'Acumulado por operario, horas por operario y metros por turno.',
    },
    {
        parametro: 'Tipo, subtipo, proceso y potencia',
        ruta: '/maquinas/parametros/tipos',
        pantalla: 'Clasificación de Máquinas',
        coleccion: 'TipoMaquina',
        afecta: 'Filtros de máquinas en Asignar Referencia y Características.',
    },
    {
        parametro: 'Reglas de programación, cargos, ciclo de rotación y ausencias',
        ruta: '/maquinas/parametros/reglas-programacion',
        pantalla: 'Reglas / Cargos / Agente / Ausencias',
        coleccion: 'ReglasProgramacion, Cargo, ConfiguracionAgente, AusenciaOperario',
        afecta: 'Generación automática de turnos del Agente de Turnos.',
    },
];

// ─── Catálogo de pantallas ───
const CATALOGO = [
    // Tablero
    {
        categoria: 'Tablero',
        nombre: 'Tablero principal',
        ruta: '/datos',
        proposito: 'Monitoreo en tiempo real de todas las máquinas (socket.io).',
        muestra: 'Temperatura, humedad, amperaje, RPM, velocidad lineal y eficiencia instantánea por máquina.',
        fuente: 'Socket en vivo + GET /api/maquinas-parametros + GET /machine8/acumulado',
        calculo: 'Color de cada indicador: valor < mín → alerta baja; valor > máx → alerta alta (parpadea); en rango → color normal.',
        parametros: ['/maquinas/parametros'],
        cuadrar: 'Si un indicador parpadea sin razón, revisa los mín/máx de esa máquina en Parámetros de Máquinas.',
    },
    // Tablas
    {
        categoria: 'Tablas',
        nombre: 'Tabla de Eficiencia',
        ruta: '/tabla-promedio-rpm/eficiencia-table',
        proposito: 'Comparar la eficiencia diaria de cada máquina en un rango de fechas.',
        muestra: 'Matriz fecha × máquina con % de eficiencia, total por fecha y total por máquina. Exporta a PDF.',
        fuente: 'GET /api/maquinas-lecturas?fechaInicio&fechaFin',
        calculo: 'Eficiencia día = promedio de las lecturas de 10 min, donde cada lectura = (promedio RPM ÷ RPM máximo) × 100. Totales = promedio simple.',
        parametros: ['/maquinas/valores-eficiencia'],
        cuadrar: 'Cambiar el RPM máximo solo afecta lecturas nuevas; los días ya guardados conservan su eficiencia.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla componentes',
        ruta: '/MaquinasTable',
        proposito: 'Inventario de máquinas registradas y sus sensores.',
        muestra: 'Nombre, número de máquina, sensor y fecha de registro.',
        fuente: 'GET /maquinas',
        calculo: 'Sin cálculo; listado directo.',
        parametros: ['/maquinas/parametros/ingreso'],
        cuadrar: 'El número de máquina debe coincidir con el usado en referencias y asignaciones (ej. "Machine1").',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Acumulado RPM',
        ruta: '/ultimo-acumulado-rpm',
        proposito: 'Ver las revoluciones acumuladas por máquina y día.',
        muestra: 'Matriz fecha × máquina con vueltas totales y totales.',
        fuente: 'GET /api/ultimo-acumulado-rpm?startDate&endDate',
        calculo: 'Suma del acumuladoRPM de las lecturas del día.',
        parametros: [],
        cuadrar: 'Es el dato bruto: si los metros no cuadran, compara contra esta tabla para saber si el problema es el sensor o la rev/metro.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Acumulado Operarios',
        ruta: '/ultimo-acumulado-operarios',
        proposito: 'Producción atribuida a cada operario según sus turnos asignados.',
        muestra: 'Máquina, referencia, revoluciones y metros producidos por operario, con total.',
        fuente: 'GET /api/ultimo-acumulado-operarios',
        calculo: 'Revoluciones dentro de la ventana del turno ÷ rev/metro de la referencia activa en esa máquina ese día. Turnos de noche cruzan medianoche.',
        parametros: ['/maquinas/asignar-operarios', '/maquinas/asignar-referencia', '/maquinas/referencias'],
        cuadrar: 'Metros en 0 = falta asignación de referencia o rev/metro para esa máquina.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla Horas Operario',
        ruta: '/tablas/horas-operario',
        proposito: 'Horas trabajadas por un operario en un rango de fechas.',
        muestra: 'Fecha, horario del turno y total de horas.',
        fuente: 'GET /api/horas-operario?operarioId&fechaInicio&fechaFin',
        calculo: 'Duración de cada turno asignado (fin − inicio), sumada por día.',
        parametros: ['/maquinas/asignar-operarios', '/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Si las horas no cuadran, revisa el horario del turno en Guardar Turnos y las ausencias del operario.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla segmentos horas',
        ruta: '/maquinas/parametros-turnos',
        proposito: 'Acumulado de producción dividido por turnos del día.',
        muestra: 'Filas turno / fecha × máquina con totales.',
        fuente: 'POST /api/acumulado-turnos-dia',
        calculo: 'Suma de vueltas de las lecturas que caen dentro de cada ventana de turno.',
        parametros: ['/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Los segmentos salen de los horarios definidos para el periodo; si no hay periodo vigente, la tabla queda vacía.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Producción por Referencia',
        ruta: '/produccion-por-referencia',
        proposito: 'Metros producidos por referencia y máquina en un rango.',
        muestra: 'Fecha, máquina, referencia, revoluciones, rev/metro y producción (m), con subtotal.',
        fuente: 'GET /api/produccion-referencia?fechaInicio&fechaFin',
        calculo: 'Producción (m) = revoluciones del día ÷ revoluciones por metro.',
        parametros: ['/maquinas/referencias', '/maquinas/asignar-referencia'],
        cuadrar: 'Rev/metro muestra el valor usado; si es 0 la referencia no tiene configurada esa máquina.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Programa de Telas',
        ruta: '/programa-telas',
        proposito: 'Ver qué referencia está programada en cada máquina.',
        muestra: 'Asignaciones de referencia vigentes en el rango.',
        fuente: 'GET /asignar-referencia?fechaInicio&fechaFin',
        calculo: 'Sin cálculo; cruce de asignaciones por fechas.',
        parametros: ['/maquinas/asignar-referencia'],
        cuadrar: 'Fechas solapadas en una misma máquina generan referencias dobles en otras tablas.',
    },
    {
        categoria: 'Tablas',
        nombre: 'Tabla de Teñido',
        ruta: '/tablas/tabla-tenido',
        proposito: 'Seguimiento de las órdenes de teñido.',
        muestra: 'Fecha/hora, máquina, referencia, color, código, cantidad (kg) y comentarios.',
        fuente: 'GET /api/asignar-tenido',
        calculo: 'Sin cálculo; listado con notas editables.',
        parametros: ['/maquinas/asignar-tenido'],
        cuadrar: '—',
    },
    // Gráficas
    {
        categoria: 'Gráficas',
        nombre: 'Gráfica Máquina Días',
        ruta: '/graficas/produccion-maquina',
        proposito: 'Tendencia diaria de producción y eficiencia por máquina.',
        muestra: 'Series por día de vueltas y eficiencia promedio.',
        fuente: 'GET /api/maquinas-lista + producción por máquina',
        calculo: 'Promedio diario de eficiencia y suma diaria de vueltas.',
        parametros: ['/maquinas/valores-eficiencia'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Gráfica Máquina Hora',
        ruta: '/graficas/maquina-dia',
        proposito: 'Detalle hora a hora de una máquina en un día.',
        muestra: 'RPM, temperatura, humedad y amperaje por hora.',
        fuente: 'Lecturas de 10 min agrupadas por hora',
        calculo: 'Promedio horario de las lecturas.',
        parametros: ['/maquinas/parametros'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Amperajes por Máquina',
        ruta: '/graficas/amperajes-maquina',
        proposito: 'Comparar el consumo eléctrico diario.',
        muestra: 'Promedio de amperaje por día y máquina.',
        fuente: 'GET /promedio-amperaje-dia',
        calculo: 'Promedio de las lecturas de amperaje del día.',
        parametros: ['/maquinas/parametros'],
        cuadrar: 'Usa esta gráfica para fijar un maxAmp realista antes de activar alarmas.',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Amperaje Máquina Día',
        ruta: '/graficas/amperaje-maquina-dia',
        proposito: 'Amperaje hora a hora de una máquina.',
        muestra: 'Curva horaria de amperaje.',
        fuente: 'GET /amperaje-por-hora',
        calculo: 'Promedio horario.',
        parametros: ['/maquinas/parametros'],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Acumulado Rama',
        ruta: '/graficas/acumulado-machine8',
        proposito: 'Metros lineales por día de la rama (Machine8).',
        muestra: 'Acumulado de metros lineales por día.',
        fuente: 'GET /api/machine8/acumulado-diario',
        calculo: 'Suma diaria de velocidad lineal.',
        parametros: [],
        cuadrar: '—',
    },
    {
        categoria: 'Gráficas',
        nombre: 'Producción por Referencia (gráfica)',
        ruta: '/graficas/produccion-referencia',
        proposito: 'Visualizar los metros por referencia.',
        muestra: 'Barras de metros por referencia en el rango.',
        fuente: 'GET /produccion-referencia + /referencias',
        calculo: 'Igual que la tabla: revoluciones ÷ rev/metro.',
        parametros: ['/maquinas/referencias', '/maquinas/asignar-referencia'],
        cuadrar: '—',
    },
    // Parámetros y Programación (en estas entradas: muestra = qué se configura, calculo = a qué afecta)
    {
        categoria: 'Programación',
        nombre: 'Ingreso de Máquina',
        ruta: '/maquinas/parametros/ingreso',
        proposito: 'Registrar las máquinas y sus sensores.',
        muestra: 'Nombre, número de máquina, número de sensor, datos a sensar y notas.',
        fuente: 'GET/POST/PUT/DELETE /api/machines',
        calculo: 'Lista de máquinas que usan todos los formularios de asignación y la Tabla componentes.',
        parametros: [],
        cuadrar: 'El número de máquina debe tener el formato "MachineN" para que coincida con lecturas y asignaciones.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Parámetros de Máquinas',
        ruta: '/maquinas/parametros',
        proposito: 'Definir los rangos normales de cada sensor por máquina.',
        muestra: 'Mín / máx de temperatura, humedad, amperaje y RPM.',
        fuente: 'GET/POST /api/maquinas-parametros',
        calculo: 'Colores y parpadeo del Tablero principal, rango de los indicadores circulares y alarma de amperaje (amperaje ≥ máx).',
        parametros: ['/maquinas/contactos-alarma'],
        cuadrar: 'Si una alarma se dispara de más, sube el máx de amperaje de esa máquina.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Valores de Eficiencia',
        ruta: '/maquinas/valores-eficiencia',
        proposito: 'Fijar el RPM máximo de cada máquina.',
        muestra: 'RPM máximo por máquina (edición individual o múltiple).',
        fuente: 'GET/POST /api/valores-eficiencia, /api/valores-eficiencia/multiple',
        calculo: 'Eficiencia = (RPM ÷ RPM máximo) × 100 en las lecturas de 10 min, el Tablero y la Tabla de Eficiencia.',
        parametros: [],
        cuadrar: 'El cambio aplica a lecturas nuevas; el backend lo toma en máx. 5 min.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Características de Máquinas',
        ruta: '/maquinas/parametros/caracteristicas',
        proposito: 'Ficha técnica de cada máquina.',
        muestra: 'Modelo, marca, galga, diámetro, potencia, proceso, subtipo y hasta 4 tipos de aguja con cantidad y origen.',
        fuente: 'GET/POST/PUT/DELETE /api/caracteristicas-maquina',
        calculo: 'Datos base de Cambio de Agujas y de Referencias.',
        parametros: ['/maquinas/parametros/tipos'],
        cuadrar: '—',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Clasificación de Máquinas',
        ruta: '/maquinas/parametros/tipos',
        proposito: 'Clasificar las máquinas por tipo.',
        muestra: 'Tipo, subtipo, proceso y potencia por número de máquina.',
        fuente: 'GET/POST/PUT/DELETE /api/tipos-maquina',
        calculo: 'Filtros de máquinas en Asignar Referencia y Características.',
        parametros: [],
        cuadrar: '—',
    },
    {
        categoria: 'Programación',
        nombre: 'Cambio de Agujas',
        ruta: '/maquinas/parametros/cambio-agujas',
        proposito: 'Historial de cambios de agujas por máquina.',
        muestra: 'Máquina, fecha de cambio y tipos de aguja con cantidad y origen.',
        fuente: 'GET/POST/PUT/DELETE /api/cambios-agujas',
        calculo: 'Solo registro histórico; no altera cálculos.',
        parametros: ['/maquinas/parametros/caracteristicas'],
        cuadrar: '—',
    },
    {
        categoria: 'Programación',
        nombre: 'Calendario de Mantenimiento',
        ruta: '/maquinas/mantenimiento',
        proposito: 'Programar mantenimientos preventivos, correctivos y predictivos.',
        muestra: 'Máquina, fechas y horas, técnico, tipo, estado y observaciones.',
        fuente: 'GET/POST/PUT/DELETE /api/mantenimiento',
        calculo: 'Los mantenimientos aparecen como bloqueos en rojo en Asignar Referencia y Asignar Teñido.',
        parametros: ['/maquinas/asignar-referencia', '/maquinas/asignar-tenido'],
        cuadrar: '—',
    },
    {
        categoria: 'Programación',
        nombre: 'Contactos de Alarma',
        ruta: '/maquinas/contactos-alarma',
        proposito: 'Quién recibe las alarmas y cada cuánto.',
        muestra: 'Nombre, teléfono (WhatsApp), chat ID de Telegram, activo y frecuencia de alarmas en minutos.',
        fuente: 'GET/POST/PUT/DELETE /api/contactos-alarma, GET/PUT /api/configuracion-alarma',
        calculo: 'Envío de alarmas por Telegram/WhatsApp; no se repite la misma alarma antes de la frecuencia configurada (10 min por defecto).',
        parametros: ['/maquinas/parametros'],
        cuadrar: 'Si no llegan alarmas, revisa que el contacto esté activo y tenga canal configurado.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Materia Prima',
        ruta: '/maquinas/parametros/ingreso-materia-prima',
        proposito: 'Catálogo de hilos y fibras.',
        muestra: 'Tipo de fibra, composición (algodón / poliéster) y título.',
        fuente: 'GET/POST/PUT/DELETE /materias-primas',
        calculo: 'Opciones de materia prima en Referencias.',
        parametros: ['/maquinas/referencias'],
        cuadrar: '—',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Referencias',
        ruta: '/maquinas/referencias',
        proposito: 'Definir cada tela y cómo se produce en cada máquina.',
        muestra: 'Estructura, materias primas y %, ancho, peso g/m², espiralidad, largo de malla; por máquina: revoluciones por metro y rendimiento práctico.',
        fuente: 'GET/POST/PUT/DELETE /referencias',
        calculo: 'metros = revoluciones ÷ rev/metro en Producción por Referencia, Acumulado Operarios y Donut metros por turno.',
        parametros: ['/maquinas/asignar-referencia', '/maquinas/parametros/ingreso-materia-prima'],
        cuadrar: 'Si los metros no cuadran con lo medido, ajusta la rev/metro de esa referencia en esa máquina.',
    },
    {
        categoria: 'Programación',
        nombre: 'Asignar Referencia',
        ruta: '/maquinas/asignar-referencia',
        proposito: 'Qué referencia teje cada máquina en cada rango de fechas.',
        muestra: 'Calendario: máquina, referencia, fecha de inicio y fin. Muestra mantenimientos como bloqueos.',
        fuente: 'GET/POST/PUT/DELETE /api/asignar-referencia',
        calculo: 'Define qué rev/metro se usa cada día. Sin asignación, la producción en metros sale en 0.',
        parametros: ['/maquinas/referencias', '/maquinas/mantenimiento'],
        cuadrar: 'Evita solapamientos de fechas en la misma máquina.',
    },
    {
        categoria: 'Programación',
        nombre: 'Asignar Teñido',
        ruta: '/maquinas/asignar-tenido',
        proposito: 'Programar teñidos por máquina.',
        muestra: 'Máquina, referencia, cantidad en kg, fechas y horas, nota.',
        fuente: 'GET/POST/PUT/DELETE /api/asignar-tenido',
        calculo: 'Alimenta la Tabla Teñido.',
        parametros: ['/maquinas/referencias', '/maquinas/mantenimiento'],
        cuadrar: '—',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Ingreso de Operario',
        ruta: '/maquinas/ingreso-operario',
        proposito: 'Registrar operarios.',
        muestra: 'Nombre, apellido, identificación, teléfono, cargo, grupo de rotación y notas.',
        fuente: 'GET/POST/PUT/DELETE /api/operators',
        calculo: 'Lista de operarios en Asignar Operarios, Ausencias y el Agente de Turnos (por cargo y grupo de rotación).',
        parametros: ['/maquinas/parametros/cargos'],
        cuadrar: '—',
    },
    {
        categoria: 'Programación',
        nombre: 'Asignar Operarios',
        ruta: '/maquinas/asignar-operarios',
        proposito: 'Qué operario trabaja en qué máquina y turno.',
        muestra: 'Calendario: fecha, turno, máquina, operario y nota.',
        fuente: 'GET/POST/PUT/DELETE /api/asignar-operario',
        calculo: 'Acumulado por operario, horas por operario y metros por turno.',
        parametros: ['/maquinas/ingreso-operario', '/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Un operario sin asignación no aparece en las tablas de operarios.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Guardar Turnos',
        ruta: '/maquinas/formulario-guardar-turnos',
        proposito: 'Definir los horarios de los turnos por periodo.',
        muestra: 'Fecha de inicio y fin del periodo, número de turnos y hora de inicio / fin de cada uno.',
        fuente: 'GET/POST /parametros-turnos, /verificar-asignaciones',
        calculo: 'Ventanas horarias de la Tabla segmentos horas, asignación de operarios y horas por operario.',
        parametros: ['/maquinas/asignar-operarios'],
        cuadrar: 'Los turnos de noche cruzan medianoche; revisa que el fin sea del día siguiente.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Cargos',
        ruta: '/maquinas/parametros/cargos',
        proposito: 'Catálogo de cargos de los operarios.',
        muestra: 'Nombre del cargo y características.',
        fuente: 'GET/POST/PUT/DELETE /api/cargos',
        calculo: 'Cargo de cada operario y cargo requerido en las Reglas de Programación.',
        parametros: ['/maquinas/ingreso-operario', '/maquinas/parametros/reglas-programacion'],
        cuadrar: '—',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Reglas de Programación',
        ruta: '/maquinas/parametros/reglas-programacion',
        proposito: 'Cuántos operarios y de qué cargo necesita cada grupo de máquinas.',
        muestra: 'Nombre de la regla, máquinas, cargo requerido, operarios por turno, activa y notas.',
        fuente: 'GET/POST/PUT/DELETE /api/reglas-programacion',
        calculo: 'Generación automática de turnos del Agente de Turnos.',
        parametros: ['/maquinas/parametros/cargos', '/maquinas/parametros/configuracion-agente'],
        cuadrar: '—',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Configuración del Agente',
        ruta: '/maquinas/parametros/configuracion-agente',
        proposito: 'Cómo rota el Agente de Turnos.',
        muestra: 'Ciclo de rotación, lunes de referencia (inicio del ciclo), incluir sábados, generar en domingos y notas.',
        fuente: 'GET/POST /api/configuracion-agente',
        calculo: 'Qué grupo de rotación cae en cada turno al generar asignaciones.',
        parametros: ['/maquinas/parametros/reglas-programacion', '/maquinas/parametros/ausencias-operario'],
        cuadrar: 'Si la rotación sale corrida, revisa el lunes de referencia.',
    },
    {
        categoria: 'Parámetros',
        nombre: 'Ausencias de Operario',
        ruta: '/maquinas/parametros/ausencias-operario',
        proposito: 'Registrar vacaciones, incapacidades y otras ausencias.',
        muestra: 'Operario, tipo de ausencia, fecha de inicio y fin, estado y nota.',
        fuente: 'GET/POST/PUT/DELETE /api/ausencias-operario',
        calculo: 'El Agente de Turnos no asigna al operario en los días dentro del rango de la ausencia.',
        parametros: ['/maquinas/ingreso-operario'],
        cuadrar: '—',
    },
    {
        categoria: 'Programación',
        nombre: 'Programación IA',
        ruta: '/ia/programacion',
        proposito: 'Recomendar en qué máquinas producir una referencia y cuánto tardará.',
        muestra: 'Referencias y cantidad en metros a producir; rango de fechas opcional para revisar disponibilidad.',
        fuente: 'POST /api/programacion-ia, GET /api/programacion-ia/referencias',
        calculo: 'Por máquina: metros por turno = (revoluciones promedio por día de los últimos 30 días ÷ 2 turnos) ÷ rev/metro; turnos necesarios = cantidad ÷ metros por turno (8 h por turno). Ordena primero las máquinas libres en el rango y luego por mayor producción ponderada por eficiencia. También calcula el consumo de hilaza por materia prima.',
        parametros: ['/maquinas/referencias', '/maquinas/asignar-referencia', '/maquinas/valores-eficiencia'],
        cuadrar: 'Una máquina sin rev/metro (ni rendimiento práctico) en la referencia, o sin lecturas en los últimos 30 días, no aparece en la recomendación.',
    },
    {
        categoria: 'Programación',
        nombre: 'Agente de Turnos',
        ruta: '/ia/agente-turnos',
        proposito: 'Generar automáticamente las asignaciones de operarios para un rango de fechas.',
        muestra: 'Rango de fechas; vista previa del plan por día y turno antes de confirmar.',
        fuente: 'GET /api/agente-turnos/generar, POST /api/agente-turnos/confirmar',
        calculo: 'Cruza reglas activas, operarios por cargo y grupo de rotación, ciclo de rotación, ausencias y horarios de turnos. Al confirmar guarda las asignaciones de operarios (opción de sobrescribir las existentes).',
        parametros: ['/maquinas/parametros/reglas-programacion', '/maquinas/parametros/configuracion-agente', '/maquinas/parametros/ausencias-operario', '/maquinas/formulario-guardar-turnos'],
        cuadrar: 'Si faltan operarios en el plan, revisa cargos, grupos de rotación y ausencias activas en esas fechas.',
    },
];

const CATEGORIAS_CONFIG = ['Parámetros', 'Programación'];

const NOMBRE_RUTA = Object.fromEntries([
    ...PARAMETROS.map(p => [p.ruta, p.pantalla]),
    ...CATALOGO.filter(c => CATEGORIAS_CONFIG.includes(c.categoria)).map(c => [c.ruta, c.nombre]),
]);

const CATEGORIAS = ['Todas', 'Tablero', 'Tablas', 'Gráficas', 'Parámetros', 'Programación'];

const Campo = ({ titulo, children }) => (
    <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#90caf9', textTransform: 'uppercase', letterSpacing: 1 }}>
            {titulo}
        </Typography>
        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>{children}</Typography>
    </Box>
);

const CatalogoTablas = () => {
    const [busqueda, setBusqueda] = useState('');
    const [categoria, setCategoria] = useState('Todas');

    const filtrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return CATALOGO.filter(item =>
            (categoria === 'Todas' || item.categoria === categoria) &&
            (!q || Object.values(item).join(' ').toLowerCase().includes(q))
        );
    }, [busqueda, categoria]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 300, mb: 1 }}>
                Catálogo de tablas
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Qué hace cada tabla, gráfica y pantalla de parámetros y programación, de dónde sale el dato y qué hay que ajustar para cuadrarla.
            </Typography>

            {/* Mapa de parámetros */}
            <Typography variant="h6" sx={{ fontWeight: 400, mb: 1 }}>Mapa de parámetros</Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Parámetro</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Se configura en</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Qué afecta</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {PARAMETROS.map(p => (
                            <TableRow key={p.parametro}>
                                <TableCell sx={{ minWidth: 180 }}>{p.parametro}</TableCell>
                                <TableCell sx={{ minWidth: 160 }}>
                                    <Button component={Link} to={p.ruta} size="small" sx={{ textTransform: 'none', p: 0 }}>
                                        {p.pantalla}
                                    </Button>
                                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                                        {p.coleccion}
                                    </Typography>
                                </TableCell>
                                <TableCell>{p.afecta}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Filtros */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                    size="small"
                    placeholder="Buscar tabla, parámetro o endpoint…"
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    sx={{ flex: '1 1 260px' }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
                {CATEGORIAS.map(c => (
                    <Chip
                        key={c}
                        label={c}
                        color={categoria === c ? 'primary' : 'default'}
                        variant={categoria === c ? 'filled' : 'outlined'}
                        onClick={() => setCategoria(c)}
                    />
                ))}
            </Box>

            {/* Catálogo */}
            {filtrados.length === 0 && (
                <Typography sx={{ color: 'text.secondary' }}>No hay resultados para la búsqueda.</Typography>
            )}
            {filtrados.map(item => {
                const esParametro = CATEGORIAS_CONFIG.includes(item.categoria);
                return (
                <Accordion key={item.ruta} disableGutters sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                            <Chip label={item.categoria} size="small" variant="outlined" />
                            <Typography sx={{ fontWeight: 500 }}>{item.nombre}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>— {item.proposito}</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Campo titulo={esParametro ? 'Qué se configura' : 'Qué muestra'}>{item.muestra}</Campo>
                        <Campo titulo={esParametro ? 'A qué afecta' : 'Cálculo'}>{item.calculo}</Campo>
                        <Campo titulo={esParametro ? 'Dónde se guarda' : 'Fuente de datos'}><code>{item.fuente}</code></Campo>
                        {item.cuadrar !== '—' && <Campo titulo="Para cuadrar">{item.cuadrar}</Campo>}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Button component={Link} to={item.ruta} variant="contained" size="small">
                                {esParametro ? 'Abrir pantalla' : 'Abrir tabla'}
                            </Button>
                            {item.parametros.map(r => (
                                <Button key={r} component={Link} to={r} variant="outlined" size="small">
                                    {NOMBRE_RUTA[r] || r}
                                </Button>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
                );
            })}
        </Container>
    );
};

export default CatalogoTablas;
